/* eslint-disable max-len */
import { Box, CircularProgress, Divider, Grid, Popover, Select, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getNetworkList } from '../../../../containers/actions/resource_graphql_actions/content_graphql_api';
import { useScroll } from '../../../../libs/CustomHooks/useScroll';
import CompanyItem from './CompanyItem';
import CompanyDetail from './CompanyDetail';
import '../../Shimmer/HelpandSupportShimmer/Shimmer.css'; // used in child components

interface CompanyType {
	companyId: string;
	createdAt: number;
	followCount: number;
	industryTypes: string[];
	isActive: string;
	name: string;
	relationId: string;
	relationName: string;
	role: string;
	seenBy: any[];
	sender: boolean;
	status: string;
	updatedAt: number;
	userId: string;
	_id: string;
	about: string;
}

interface NetworkCompanyType {
	companyId: string;
	relationName: string;
	isMeSender: boolean;
	companyName: string;
	count: {
		category: number;
		article: number;
		video: number;
		podcast: number;
		document: number;
	};
}

interface CustomCompanyFilterProps {
    menuItems?: any[];
    labelText?: string;
    value?: NetworkCompanyType;
    onChange?: (company: NetworkCompanyType) => void;
}

const CustomCompanyFilter: React.FC<CustomCompanyFilterProps> = (props) =>
{
	const {
		menuItems,
		labelText,
		value: externalValue,
		onChange: externalOnChange
	} = props;

	const location = useLocation();
	const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>(null);
	const selectedCompanyId = useParams()?.companyId;
	const [ internalSelectedCompany, setInternalSelectedCompany ] = useState<string | null>(selectedCompanyId || null);
	const [ companyData, setCompanyData ] = useState<NetworkCompanyType[]>([]);
	const [ hoverCompany, setHoverCompany ] = useState<NetworkCompanyType | null>(null);
	const [ loading, setLoading ] = useState<boolean>(false);
	const [ error, setError ] = useState<string | null>(null);
	const [ page, setPage ] = useState<number>(0);
	const [ count, setCount ] = useState<number>(0);
	const itemPerPage = 10;

	const lastEleRef = useRef(null);
	const loaderRef = useRef<boolean>(false);
	const navigate = useNavigate();
	// Get company data from URL state

	// Use external value if provided, otherwise use internal state
	const selectedCompany = externalValue !== undefined ? externalValue : internalSelectedCompany;

	// Get session data from Redux store
	const session = useSelector((state: any) => state?.mysession?.session);
	// const companyId = session?.companyId || '';

	// Apply useScroll hook for infinite scrolling
	useScroll({
		lastEleRef,
		loaderRef,
		count,
		data : companyData,
		setPage
	});

	const getCompanyCount = async () =>
	{
		try
		{
			// Prepare options for getting count only
			const countOptions: any = {
				limit : 0,
				skip  : 0,
				count : true
			};

			const countResponse = await getNetworkList(countOptions);

			if (countResponse?.success && countResponse?.count !== null)
			{
				setCount(countResponse.count);
			}
			else
			{
				setCount(0);
			}
		}
		catch (err)
		{
			console.error('Error fetching company count:', err);
			setCount(0);
		}
	};

	const fetchCompanyData = async () =>
	{
		setLoading(true);
		loaderRef.current = true;

		try
		{
			// Prepare options for getNetworkList API
			const options: any = {
				limit : itemPerPage,
				skip  : page,
				count : false
			};

			const response = await getNetworkList(options);

			if (response?.success && response?.data)
			{
				// Use the network data directly without transformation
				const companies = response.data as NetworkCompanyType[];

				// Append or replace data based on page number
				setCompanyData((prev) =>
					(page === 0 ? companies : [ ...prev, ...companies ]));

				if (page === 0)
				{
					if (companies.length > 0)
					{
						// Set first company as selected if data exists and no external value is provided
						const firstCompany = companies[0];

						setHoverCompany(firstCompany);
					}
					if (companies.length >= itemPerPage)
					{
						getCompanyCount();
					}
					else
					{
						setCount(companies.length);
					}
				}
			}
			else
			{
				setError('Failed to load companies');
			}
		}
		catch (err)
		{
			console.error('Error fetching companies:', err);
			setError('Failed to load companies');
		}
		finally
		{
			setLoading(false);
			loaderRef.current = false;
		}
	};

	// Fetch companies when component mounts or page changes
	useEffect(() =>
	{
		fetchCompanyData();
	}, [ page ]);

	const handleSelectOpen = (event: React.SyntheticEvent) =>
	{
		setAnchorEl(event.currentTarget as HTMLElement);
	};

	const handleClose = () =>
	{
		setAnchorEl(null);
	};

	const handleCompanySelect = (company: NetworkCompanyType) =>
	{
		// Update internal state if we're not using external value
		if (externalValue === undefined)
		{
			setInternalSelectedCompany(company.companyId);
			navigate(`/resource/network/companies/${company.companyId}`, {
				state : { companyData: company }
			});
		}

		// Call external handler if provided
		if (externalOnChange)
		{
			externalOnChange(company);
		}

		handleClose();
	};

	useEffect(() =>
	{
		setInternalSelectedCompany(selectedCompanyId || null);
	}, [ selectedCompanyId ]);

	const open = Boolean(anchorEl);
	const id = open ? 'custom-popover' : undefined;

	// Use API data if available, otherwise use menuItems prop
	const displayItems = companyData.length > 0 ? companyData : [];

	return (
		<>
			<Grid item>
				<Typography color='var(--darkgray)' fontSize='var(--pixel10)'>
					{labelText}
				</Typography>
				<Select
					value={selectedCompany || 'all'}
					open={false}
					onClick={handleSelectOpen}
					renderValue={() =>
					{
						// Priority: URL state data > selected company > default
						if (selectedCompany)
						{
							return selectedCompany ? displayItems.find((item) => item.companyId === selectedCompany)?.companyName || 'All' : 'All';
						}

						return 'All';
					}}
					sx={{
						width                                : 200,
						backgroundColor                      : 'var(--lightgray)',
						border                               : 'none',
						borderRadius                         : 'var(--pixel4)',
						boxShadow                            : 'none',
						minWidth                             : '220px',
						'& .MuiOutlinedInput-notchedOutline' : {
							border : 'none'
						},
						'&:hover' : {
							backgroundColor : '#3FBBE11A'
						},
						'& .MuiSelect-select' : {
							padding  : '4px 10px',
							fontSize : 'var(--pixel14)'
						}
					}}
					MenuProps={{
						disableAutoFocusItem : true,
						PaperProps           : {
							style       : { maxHeight: 300, overflow: 'auto' },
							onMouseDown : (e) => e.stopPropagation()
						}
					}}
				/>

				<Popover
					id={id}
					open={open}
					anchorEl={anchorEl}
					onClose={handleClose}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				>
					<Grid container px={1.5} py={2} sx={{ width: '600px', height: '380px', border: '1px solid #256EA61A', borderRadius: '6px', overflow: 'hidden' }}>
						<Grid item xs={5} sx={{
							height       : '100%',
							overflowY    : 'hidden',
							paddingRight : '8px',
							'&:hover'    : {
								overflowY                    : 'auto',
								'&::-webkit-scrollbar-track' : { background: 'transparent' },
								'&::-webkit-scrollbar-thumb' : { background: '#D9D9D9', borderRadius: '4px' }
							}
						}}
						>
							{loading && page === 0
								? (
									<Box display='flex' justifyContent='center' alignItems='center' height='100%'>
										<CircularProgress size={24} />
									</Box>
								)
								: error
									? (
										<Box display='flex' justifyContent='center' alignItems='center' height='100%'>
											<Typography color='error'>{error}</Typography>
										</Box>
									)
									: displayItems.length === 0
										? (
											<Box display='flex' justifyContent='center' alignItems='center' height='100%'>
												<Typography
													color='var(--darkgray)'
													fontSize='var(--pixel14)'
													textAlign='center'
												>
													No companies found
												</Typography>
											</Box>
										)
										: (
											<Stack spacing={1} sx={{ height: '100%' }}>
												{displayItems.map((company, index) => (
													<CompanyItem
														key={company.companyId || index}
														company={company}
														onSelect={handleCompanySelect}
														onMouseEnter={() => setHoverCompany(company)}
													/>
												))}

												{/* Invisible element for intersection observer */}
												{!loading && displayItems.length > 0 && displayItems.length < count && (
													<Box ref={lastEleRef} sx={{ border: '1px solid white', height: '4px', width: '100%' }} />
												)}

												{/* Show loading indicator at bottom when fetching more */}
												{loading && page > 0 && (
													<Box display='flex' justifyContent='center' py={1}>
														<CircularProgress size={20} />
													</Box>
												)}
											</Stack>
										)}
						</Grid>
						<Grid item xs={0.2}>
							<Divider orientation='vertical' flexItem sx={{ backgroundColor: '#e0e0e0' }} />
						</Grid>
						<Grid item xs={6.8} sx={{ borderLeft: '1px solid #e0e0e0', height: '100%' }}>
							{hoverCompany && (
								<CompanyDetail
									selectedCompany={hoverCompany}
									labelText={labelText || ''}
								/>
							)}
						</Grid>
					</Grid>
				</Popover>
			</Grid>
		</>
	);
};

export default CustomCompanyFilter;